import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  GitCompare, Shield, Languages, Download, Workflow, Lightbulb,
  Loader2, Copy, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ToolsPanel = () => {
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const token = localStorage.getItem('reasondms_token');

  // Version Compare state
  const [textV1, setTextV1] = useState('');
  const [textV2, setTextV2] = useState('');
  const [compareResult, setCompareResult] = useState(null);

  // Redaction state
  const [redactText, setRedactText] = useState('');
  const [redactResult, setRedactResult] = useState(null);

  // Translation state
  const [translateText, setTranslateText] = useState('');
  const [targetLang, setTargetLang] = useState('hi');
  const [translateResult, setTranslateResult] = useState(null);
  const [languages, setLanguages] = useState({});

  // Workflow state
  const [workflowText, setWorkflowText] = useState('');
  const [workflowResult, setWorkflowResult] = useState(null);

  // Export state
  const [exportFormat, setExportFormat] = useState('json');
  const [exportResult, setExportResult] = useState(null);
  const [exportDocCount, setExportDocCount] = useState(50);

  const handleExport = async () => {
    setLoading(true);
    setActiveAction('Export');
    try {
      // Fetch actual documents from the API
      const docRes = await fetch(`${API_URL}/api/documents?limit=${exportDocCount}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const docs = await docRes.json();
      const docList = Array.isArray(docs) ? docs : (docs.documents || docs.data || []);
      
      if (docList.length === 0) {
        toast.error('No documents available for export');
        return;
      }

      const exportDocs = docList.map(d => ({
        title: d.title || 'Untitled',
        type: d.doc_type || 'Other',
        text: d.description || '',
        metadata: {
          risk_level: d.risk_level || 'Low',
          tags: d.tags || [],
          created_at: d.created_at || ''
        }
      }));

      await callApi('export', { documents: exportDocs, export_format: exportFormat }, setExportResult, 'Export');
    } catch (err) {
      toast.error('Failed to export: ' + err.message);
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  // Feedback state
  const [feedbackStats, setFeedbackStats] = useState(null);

  const [activeTool, setActiveTool] = useState('compare');

  useEffect(() => {
    fetch(`${API_URL}/api/enterprise/translate/languages`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(d => d && setLanguages(d.languages || {})).catch(() => {});
    fetch(`${API_URL}/api/enterprise/feedback/stats`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(d => d && setFeedbackStats(d)).catch(() => {});
  }, []);

  const callApi = async (endpoint, body, setter, label) => {
    setLoading(true);
    setActiveAction(label);
    try {
      const res = await fetch(`${API_URL}/api/enterprise/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        let errorMsg = `${label} failed`;
        try { errorMsg = (await res.json()).detail || errorMsg; } catch { errorMsg = res.status === 404 ? 'Enterprise AI service not available' : `Server error (${res.status})`; }
        throw new Error(errorMsg);
      }
      setter(await res.json());
      toast.success(`${label} complete`);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); setActiveAction(null); }
  };

  const tools = [
    { key: 'compare', label: 'Version Compare', icon: GitCompare },
    { key: 'redact', label: 'Redaction', icon: Shield },
    { key: 'translate', label: 'Translation', icon: Languages },
    { key: 'workflow', label: 'Workflow AI', icon: Workflow },
    { key: 'export', label: 'Export', icon: Download },
    { key: 'feedback', label: 'AI Feedback', icon: Lightbulb },
  ];

  return (
    <div className="space-y-4">
      {/* Tool Selector */}
      <div className="flex flex-wrap gap-2">
        {tools.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`tool-${key}-btn`}
            onClick={() => setActiveTool(key)}
            className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${activeTool === key ? 'bg-cyan-50 border-cyan-300 text-cyan-700 font-medium' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>

      {/* Version Compare */}
      {activeTool === 'compare' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><GitCompare className="h-4 w-4 text-cyan-600" /> Version Comparison</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Version 1 (Original)</label>
                <textarea data-testid="compare-v1" value={textV1} onChange={e => setTextV1(e.target.value)}
                  className="w-full h-28 text-xs p-2 border rounded-lg font-mono resize-y outline-none focus:ring-2 focus:ring-cyan-300" placeholder="Original document text..." />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Version 2 (Modified)</label>
                <textarea data-testid="compare-v2" value={textV2} onChange={e => setTextV2(e.target.value)}
                  className="w-full h-28 text-xs p-2 border rounded-lg font-mono resize-y outline-none focus:ring-2 focus:ring-cyan-300" placeholder="Modified document text..." />
              </div>
            </div>
            <Button data-testid="compare-btn" onClick={() => callApi('version-compare', { text_v1: textV1, text_v2: textV2 }, setCompareResult, 'Compare')} disabled={!textV1.trim() || !textV2.trim() || loading} className="bg-cyan-600 hover:bg-cyan-700">
              {loading && activeAction === 'Compare' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitCompare className="h-4 w-4 mr-2" />}Compare Versions
            </Button>
            {compareResult?.success && (
              <div data-testid="compare-result" className="space-y-2">
                <div className="flex items-center gap-3">
                  <Badge className="text-xs bg-cyan-100 text-cyan-700">{compareResult.total_changes} changes</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{compareResult.change_severity}</Badge>
                </div>
                <p className="text-xs text-slate-600">{compareResult.changes_summary}</p>
                {compareResult.changes?.map((c, i) => (
                  <div key={i} className="p-2 bg-slate-50 rounded text-xs">
                    <Badge variant="outline" className="text-xs mb-1">{c.type?.replace(/_/g, ' ')}</Badge>
                    {c.old_text && <p className="text-red-600 line-through">{c.old_text}</p>}
                    {c.new_text && <p className="text-emerald-600">{c.new_text}</p>}
                    <p className="text-slate-400 mt-0.5">{c.impact}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Redaction */}
      {activeTool === 'redact' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-red-600" /> PII Detection & Redaction</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <textarea data-testid="redact-input" value={redactText} onChange={e => setRedactText(e.target.value)}
              className="w-full h-28 text-sm p-3 border rounded-lg font-mono resize-y outline-none focus:ring-2 focus:ring-red-300" placeholder="Paste text containing PII (PAN, Aadhaar, emails, phone numbers)..." />
            <Button data-testid="redact-btn" onClick={() => callApi('redact', { text: redactText, redact: true }, setRedactResult, 'Redact')} disabled={!redactText.trim() || loading} className="bg-red-600 hover:bg-red-700">
              {loading && activeAction === 'Redact' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}Detect & Redact PII
            </Button>
            {redactResult?.success && (
              <div data-testid="redact-result" className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-700 text-xs">{redactResult.total_pii_found} PII found</Badge>
                  {redactResult.pii_types_found?.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t.replace(/_/g, ' ')}</Badge>)}
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-sm font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">{redactResult.redacted_text}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Translation */}
      {activeTool === 'translate' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Languages className="h-4 w-4 text-blue-600" /> Document Translation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <textarea data-testid="translate-input" value={translateText} onChange={e => setTranslateText(e.target.value)}
              className="w-full h-28 text-sm p-3 border rounded-lg font-mono resize-y outline-none focus:ring-2 focus:ring-blue-300" placeholder="Enter text to translate..." />
            <div className="flex items-center gap-3">
              <select data-testid="translate-lang-select" value={targetLang} onChange={e => setTargetLang(e.target.value)} className="text-sm border rounded-lg px-3 py-2 bg-white">
                {Object.entries(languages).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
              <Button data-testid="translate-btn" onClick={() => callApi('translate', { text: translateText, target_language: targetLang }, setTranslateResult, 'Translate')} disabled={!translateText.trim() || loading} className="bg-blue-600 hover:bg-blue-700">
                {loading && activeAction === 'Translate' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Languages className="h-4 w-4 mr-2" />}Translate
              </Button>
            </div>
            {translateResult?.success && (
              <div data-testid="translate-result" className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 text-xs">{translateResult.source_language_name} to {translateResult.target_language_name}</Badge>
                  <Badge variant="outline" className="text-xs">Confidence: {Math.round((translateResult.confidence || 0) * 100)}%</Badge>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">{translateResult.translated_text}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workflow Suggestion */}
      {activeTool === 'workflow' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Workflow className="h-4 w-4 text-teal-600" /> AI Workflow Suggestion</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <textarea data-testid="workflow-input" value={workflowText} onChange={e => setWorkflowText(e.target.value)}
              className="w-full h-28 text-sm p-3 border rounded-lg font-mono resize-y outline-none focus:ring-2 focus:ring-teal-300" placeholder="Paste document text to get workflow suggestions..." />
            <Button data-testid="workflow-btn" onClick={() => callApi('workflow/suggest', { text: workflowText, doc_type: 'general' }, setWorkflowResult, 'Suggest')} disabled={!workflowText.trim() || loading} className="bg-teal-600 hover:bg-teal-700">
              {loading && activeAction === 'Suggest' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Workflow className="h-4 w-4 mr-2" />}Suggest Workflow
            </Button>
            {workflowResult?.success && (
              <div data-testid="workflow-result" className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-teal-100 text-teal-700 text-xs">{workflowResult.suggested_workflow?.name}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{workflowResult.suggested_workflow?.priority}</Badge>
                </div>
                <p className="text-xs text-slate-600">{workflowResult.routing_logic}</p>
                {workflowResult.suggested_workflow?.steps?.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs">
                    <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-medium shrink-0">{s.step}</span>
                    <div className="flex-1">
                      <span className="font-medium capitalize">{s.action}</span>
                      <span className="text-slate-400 ml-2">{s.assignee_role}</span>
                    </div>
                    <span className="text-slate-400">{s.sla_hours}h SLA</span>
                    {s.is_mandatory && <Badge className="bg-red-100 text-red-600 text-xs">Required</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Export */}
      {activeTool === 'export' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Download className="h-4 w-4 text-purple-600" /> Data Export</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Export document data to various formats for ERP, accounting, and analytics integration.</p>
            <div className="flex items-center gap-3">
              <select data-testid="export-format-select" value={exportFormat} onChange={e => setExportFormat(e.target.value)} className="text-sm border rounded-lg px-3 py-2 bg-white">
                {['json','csv','xml','erp_json','accounting_json'].map(f => <option key={f} value={f}>{f.toUpperCase().replace('_', ' ')}</option>)}
              </select>
              <Button data-testid="export-btn" onClick={handleExport} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                {loading && activeAction === 'Export' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export
              </Button>
            </div>
            {exportResult?.success && (
              <div data-testid="export-result">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-purple-100 text-purple-700 text-xs">{exportResult.format}</Badge>
                  <span className="text-xs text-slate-500">{exportResult.document_count} documents</span>
                </div>
                <pre className="p-3 bg-slate-50 rounded-lg text-xs font-mono max-h-40 overflow-auto">{typeof exportResult.data === 'string' ? exportResult.data : JSON.stringify(exportResult.data, null, 2)}</pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Feedback */}
      {activeTool === 'feedback' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Lightbulb className="h-4 w-4 text-amber-500" /> AI Learning Feedback</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">When you correct AI outputs, feedback is stored to improve future accuracy.</p>
            {feedbackStats?.success && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <span className="text-xl font-bold text-amber-700">{feedbackStats.total_feedback || 0}</span>
                  <p className="text-xs text-amber-600">Total Feedback</p>
                </div>
                {Object.entries(feedbackStats.by_type || {}).map(([type, count]) => (
                  <div key={type} className="text-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-lg font-bold text-slate-700">{count}</span>
                    <p className="text-xs text-slate-500 capitalize">{type.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            )}
            <Button data-testid="submit-feedback-btn" onClick={() => callApi('feedback', { feedback_type: 'general', original_output: { text: 'original' }, corrected_output: { text: 'corrected' } }, () => {
              toast.success('Feedback submitted');
              fetch(`${API_URL}/api/enterprise/feedback/stats`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : null).then(d => d && setFeedbackStats(d)).catch(() => {});
            }, 'Feedback')} disabled={loading} variant="outline" className="text-xs">
              <Lightbulb className="h-3 w-3 mr-1" /> Submit Test Feedback
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ToolsPanel;
